import React, { useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

/**
 * SimpleMetricTooltip - Transparent metric explanation component
 *
 * Displays an info icon (ⓘ) that shows a tooltip on hover with:
 * - Metric title
 * - Formula (in monospace box)
 * - Description
 * - Optional click handler for sample data
 *
 * Uses React.createPortal to render the tooltip body to document.body so it
 * escapes the overflow:hidden of liquid-glass card ancestors. Position is
 * computed via getBoundingClientRect() and applied as position:fixed so
 * viewport coords are used directly.
 *
 * Used in DM Analytics for simple hover-based metric explanations.
 * For portal-based tooltips with real samples, see MetricTooltip.tsx.
 */

interface SimpleMetricTooltipProps {
  title: string;
  formula: string;
  description: string;
  onClick?: () => void;
}

const TOOLTIP_WIDTH = 288; // matches w-72 (18rem)
const TOOLTIP_HEIGHT = 180; // estimated height — overestimate to flip eagerly

const SimpleMetricTooltip: React.FC<SimpleMetricTooltipProps> = ({
  title,
  formula,
  description,
  onClick,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();

    // Default: below trigger, left edge 2px left of trigger's left edge
    let top = rect.bottom + 8;
    let left = rect.left - 2;

    // Flip above if tooltip would clip bottom of viewport
    if (top + TOOLTIP_HEIGHT > window.innerHeight - 16) {
      top = rect.top - TOOLTIP_HEIGHT - 8;
    }

    // Flip left-aligned to right edge if tooltip would clip right of viewport
    if (left + TOOLTIP_WIDTH > window.innerWidth - 16) {
      left = rect.right - TOOLTIP_WIDTH;
    }

    setPosition({ top, left });
  }, []);

  const handleMouseEnter = useCallback(() => {
    computePosition();
    setIsVisible(true);
  }, [computePosition]);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  const handleFocus = useCallback(() => {
    computePosition();
    setIsVisible(true);
  }, [computePosition]);

  const handleBlur = useCallback(() => {
    setIsVisible(false);
  }, []);

  return (
    <span className="inline-block ml-1">
      <span
        ref={triggerRef}
        tabIndex={0}
        role={onClick ? "button" : "img"}
        aria-label={onClick ? "Show metric samples" : `${title} info`}
        className={`cursor-pointer text-glass-muted hover:text-blue-400 focus:outline-none focus-visible:text-blue-400 ${onClick ? "hover:scale-110 transition-transform" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        onKeyDown={(e) => {
          if (onClick && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            e.stopPropagation();
            onClick();
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        title={onClick ? "Click to see samples" : undefined}
      >
        ⓘ
      </span>

      {isVisible &&
        createPortal(
          <div
            className="tt-surface text-xs w-72 max-w-[calc(100vw-2rem)] p-3 z-[60] pointer-events-none"
            style={{ position: "fixed", top: position.top, left: position.left }}
          >
            <p className="font-bold text-glass mb-1">{title}</p>
            <p className="font-mono text-glass-secondary bg-black/[0.04] px-2 py-1 rounded mb-2">
              {formula}
            </p>
            <p className="text-glass-secondary">{description}</p>
            {onClick && (
              <p className="text-blue-400 font-medium mt-2">Click to see sample conversations</p>
            )}
          </div>,
          document.body
        )}
    </span>
  );
};

export default SimpleMetricTooltip;
